import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDonation, useShipDonation, useDeliverDonation, useReceiveDonation, useVerifyDonation, useCancelDonation, useUpdateTracking, useAuditLog } from '../../../api/donations';
import { useIrsForms, useEmailIrsForm, downloadIrsForm, previewIrsForm } from '../../../api/irs';
import { useDonationMessages, usePostDonationMessage } from '../../../api/messages';
import { useAuth } from '../../../hooks/useAuth';
import { UserRole, DonationStatus } from '@spi/shared';
import type { IrsFormData } from '@spi/shared';
import { StatusBadge } from '../shared/StatusBadge';
import { DonationTimeline } from './DonationTimeline';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { PageMeta } from '../shared/PageMeta';
import { IrsFormModal } from '../irs/IrsFormModal';
import { OrgIcon, DonorIcon, RecipientIcon } from '../shared/EntityIcons';
import { MessageThread } from '../messages/MessageThread';

export function DonationDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const donationId = parseInt(id || '0', 10);
  const { data: donation, isLoading } = useDonation(donationId);
  const { data: donationMessages, isLoading: donationMessagesLoading } = useDonationMessages(donationId);
  const postDonationMessage = usePostDonationMessage(donationId);
  const { data: auditLog } = useAuditLog(donationId);
  const shipMutation = useShipDonation();
  const deliverMutation = useDeliverDonation();
  const receiveMutation = useReceiveDonation();
  const verifyMutation = useVerifyDonation();
  const cancelMutation = useCancelDonation();
  const updateTrackingMutation = useUpdateTracking();
  const emailIrsMutation = useEmailIrsForm();
  const { data: irsForms } = useIrsForms();
  const [downloading, setDownloading] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [tracking, setTracking] = useState('');
  const [showShip, setShowShip] = useState(false);
  const [showDeliver, setShowDeliver] = useState(false);
  const [deliverTracking, setDeliverTracking] = useState('');
  const [deliverNotes, setDeliverNotes] = useState('');
  const [showReceive, setShowReceive] = useState(false);
  const [receiveNotes, setReceiveNotes] = useState('');
  const [receiveFiles, setReceiveFiles] = useState<File[]>([]);
  const [editingTracking, setEditingTracking] = useState(false);
  const [editTrackingValue, setEditTrackingValue] = useState('');

  // IRS Form Modal state
  const [showIrsModal, setShowIrsModal] = useState(false);
  const [irsModalExistingData, setIrsModalExistingData] = useState<IrsFormData | null>(null);

  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Cleanup preview object URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" />
      </div>
    );
  }

  if (!donation) {
    return <div className="container mx-auto px-4 py-16 text-center"><h2 className="text-2xl">Donation Not Found</h2></div>;
  }

  const isDonor = user?.id === donation.donorId;
  const isRecipient = user?.id === donation.recipientId;
  const isAdmin = user?.role === UserRole.ADMIN;
  const irsForm = irsForms?.find((f) => f.donationId === donation.id);
  const irsExists = !!irsForm;

  const handleOpenGenerateModal = () => {
    setIrsModalExistingData(null);
    setShowIrsModal(true);
  };

  const handleOpenRegenerateModal = () => {
    // Pass existing formData so the modal pre-fills from last generated form
    setIrsModalExistingData(irsForm?.formData ?? null);
    setShowIrsModal(true);
  };

  const handleIrsModalGenerated = () => {
    setShowIrsModal(false);
    setIrsModalExistingData(null);
    // Clear stale preview so next preview fetches the newly generated PDF
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = await previewIrsForm(donation.id);
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleEmail = async () => {
    try {
      await emailIrsMutation.mutateAsync(donation.id);
      toast.success('IRS form emailed to donor');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send email');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <PageMeta title={`Donation #${donation.id}`} description={`Seed donation for ${donation.projectTitle}`} path={`/marketplace/donations/${donation.id}`} />

      <Link to="/marketplace/donations" className="text-sm text-spi-green hover:underline mb-4 inline-block">&larr; Back to donations</Link>

      <div className="card p-8 mb-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl mb-1">Donation #{donation.id}</h2>
            <p className="text-spi-slate">
              <Link to={`/marketplace/projects/${donation.projectId}`} className="text-spi-green hover:underline">{donation.projectTitle}</Link>
              {donation.askCrop && <span> &middot; {donation.askCrop}</span>}
            </p>
          </div>
          <StatusBadge status={donation.status} />
        </div>

        {/* Timeline */}
        <div className="mb-8">
          <DonationTimeline donation={donation} />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-3">
            <div>
              <span className="text-xs font-semibold text-spi-slate">Donor</span>
              <p className="font-medium">
                <DonorIcon className="w-4 h-4 inline-block align-text-bottom text-violet-500 mr-1" />
                <Link to={`/marketplace/users/${donation.donorId}`} className="text-spi-green hover:underline">{donation.donorName}</Link>
                {' ('}
                <Link to={`/marketplace/organizations/${donation.donorOrgId}`} className="inline-flex items-center gap-0.5 text-spi-green hover:underline">
                  <OrgIcon className="w-3.5 h-3.5" />{donation.donorOrganization}
                </Link>
                {')'}
              </p>
            </div>
            <div>
              <span className="text-xs font-semibold text-spi-slate">Recipient</span>
              <p className="font-medium">
                <RecipientIcon className="w-4 h-4 inline-block align-text-bottom text-emerald-600 mr-1" />
                <Link to={`/marketplace/users/${donation.recipientId}`} className="text-spi-green hover:underline">{donation.recipientName}</Link>
                {' ('}
                <Link to={`/marketplace/organizations/${donation.recipientOrgId}`} className="inline-flex items-center gap-0.5 text-spi-green hover:underline">
                  <OrgIcon className="w-3.5 h-3.5" />{donation.recipientOrganization}
                </Link>
                {')'}
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <div><span className="text-xs font-semibold text-spi-slate">Crop</span><p className="font-medium">{donation.askCrop}</p></div>
            <div><span className="text-xs font-semibold text-spi-slate">Quantity</span><p className="font-medium">{donation.quantityKg > 0 ? `${donation.quantityKg} kg` : ''}{donation.quantityKg > 0 && donation.quantityPackets > 0 ? ' + ' : ''}{donation.quantityPackets > 0 ? `${donation.quantityPackets} packets` : ''}</p></div>
            <div><span className="text-xs font-semibold text-spi-slate">Fair Market Value</span><p className="font-medium">${donation.fairMarketValueUsd.toLocaleString()}</p></div>
            {(() => {
              const canEditTracking = (isDonor || isAdmin) && [DonationStatus.SHIPPED, DonationStatus.DELIVERED, DonationStatus.RECEIVED, DonationStatus.VERIFIED].includes(donation.status as DonationStatus);
              const showTracking = donation.shippingTracking || canEditTracking;
              if (!showTracking) return null;
              return (
                <div>
                  <span className="text-xs font-semibold text-spi-slate">Tracking</span>
                  {editingTracking ? (
                    <div className="flex items-center gap-2 mt-1">
                      <input value={editTrackingValue} onChange={(e) => setEditTrackingValue(e.target.value)} className="input text-sm py-1 flex-1" placeholder="Tracking number" />
                      <button
                        onClick={async () => {
                          if (!editTrackingValue.trim()) return;
                          await updateTrackingMutation.mutateAsync({ id: donation.id, tracking: editTrackingValue.trim() });
                          toast.success('Tracking updated');
                          setEditingTracking(false);
                        }}
                        disabled={updateTrackingMutation.isPending}
                        className="text-xs font-semibold text-spi-green hover:underline"
                      >
                        Save
                      </button>
                      <button onClick={() => setEditingTracking(false)} className="text-xs text-spi-slate hover:underline">Cancel</button>
                    </div>
                  ) : (
                    <p className="font-medium">
                      {donation.shippingTracking || <span className="text-spi-slate italic">Not set</span>}
                      {canEditTracking && (
                        <button
                          onClick={() => { setEditTrackingValue(donation.shippingTracking || ''); setEditingTracking(true); }}
                          className="ml-2 text-xs text-spi-green hover:underline"
                        >
                          {donation.shippingTracking ? 'Edit' : 'Add'}
                        </button>
                      )}
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {isDonor && donation.status === DonationStatus.PLEDGED && (
            <button onClick={() => setShowShip(true)} className="btn btn-primary">Mark as Shipped</button>
          )}
          {(isDonor || isAdmin) && donation.status === DonationStatus.SHIPPED && (
            <button onClick={() => setShowDeliver(true)} className="btn btn-primary">
              Mark as Delivered
            </button>
          )}
          {(isRecipient || isAdmin) && donation.status === DonationStatus.DELIVERED && (
            <button onClick={() => setShowReceive(true)} className="btn btn-primary">
              Confirm Receipt
            </button>
          )}
          {(isAdmin || isDonor) && donation.status === DonationStatus.RECEIVED && (
            <button
              onClick={async () => {
                await verifyMutation.mutateAsync(donation.id);
                toast.success('Donation verified');
              }}
              disabled={verifyMutation.isPending}
              className="btn btn-primary"
            >
              Verify Donation
            </button>
          )}
          {isAdmin && donation.status === DonationStatus.VERIFIED && !irsExists && (
            <button
              onClick={handleOpenGenerateModal}
              className="btn btn-secondary"
            >
              Generate IRS Form
            </button>
          )}
          {isAdmin && donation.status === DonationStatus.VERIFIED && irsExists && (
            <>
              <span className="inline-flex items-center gap-1.5 text-sm text-spi-green font-medium px-4 py-3">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                IRS Form Generated
              </span>
              <button
                onClick={handleOpenRegenerateModal}
                className="btn btn-outline"
              >
                Regenerate IRS Form
              </button>
            </>
          )}
          {(isDonor || isAdmin) && donation.status === DonationStatus.VERIFIED && irsExists && (
            <>
              <button
                onClick={handlePreview}
                disabled={loadingPreview}
                className="btn btn-outline"
              >
                {loadingPreview ? 'Loading...' : 'Preview IRS Form'}
              </button>
              <button
                onClick={async () => {
                  setDownloading(true);
                  try {
                    await downloadIrsForm(donation.id);
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : 'Download failed');
                  } finally {
                    setDownloading(false);
                  }
                }}
                disabled={downloading}
                className="btn btn-outline"
              >
                {downloading ? 'Downloading...' : 'Download IRS Form'}
              </button>
            </>
          )}
          {isAdmin && donation.status === DonationStatus.VERIFIED && irsExists && (
            <button
              onClick={handleEmail}
              disabled={emailIrsMutation.isPending}
              className="btn btn-outline"
            >
              {emailIrsMutation.isPending ? 'Sending...' : 'Email to Donor'}
            </button>
          )}
          {(isDonor || isAdmin) && donation.status !== DonationStatus.VERIFIED && donation.status !== DonationStatus.CANCELLED && (
            <button onClick={() => setShowCancel(true)} className="btn text-red-600 hover:bg-red-50 border border-red-200 rounded-xl px-6 py-3 font-semibold text-sm">
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="card p-8 mb-6">
        <MessageThread
          messages={donationMessages?.data || []}
          isLoading={donationMessagesLoading}
          onPost={async (body, files) => { await postDonationMessage.mutateAsync({ body, files }); }}
          isPosting={postDonationMessage.isPending}
          title="Messages"
          subtitle="Visible to the donor, recipient, and admins of this donation"
          placeholder="Send a message about this donation..."
          contextType="donation"
          contextId={donationId}
        />
      </div>

      {/* Activity Log */}
      {auditLog && auditLog.length > 0 && (
        <div className="card p-8">
          <h3 className="text-lg font-semibold mb-4">Activity Log</h3>
          <div className="space-y-3">
            {auditLog.map((entry) => (
              <div key={entry.id} className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-spi-green mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-spi-charcoal">
                    <span className="font-medium">{entry.actorName || 'System'}</span>
                    <span className="text-spi-slate"> ({entry.actorRole})</span>
                    {entry.fromStatus ? (
                      <span> changed status from <span className="font-medium">{entry.fromStatus}</span> to <span className="font-medium">{entry.toStatus}</span></span>
                    ) : (
                      <span> created donation as <span className="font-medium">{entry.toStatus}</span></span>
                    )}
                  </p>
                  {entry.note && <p className="text-spi-slate mt-0.5">{entry.note}</p>}
                  <p className="text-xs text-spi-slate mt-0.5">{new Date(entry.createdAt).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ship modal */}
      {showShip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowShip(false)} />
          <div className="relative bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4">Mark as Shipped</h3>
            <div className="mb-4">
              <label className="input-label">Tracking Number (optional)</label>
              <input value={tracking} onChange={(e) => setTracking(e.target.value)} className="input" placeholder="e.g., 1Z999AA10123456784" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowShip(false)} className="btn btn-outline flex-1">Cancel</button>
              <button
                onClick={async () => {
                  await shipMutation.mutateAsync({ id: donation.id, tracking: tracking || undefined });
                  toast.success('Marked as shipped');
                  setShowShip(false);
                }}
                disabled={shipMutation.isPending}
                className="btn btn-primary flex-1"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deliver modal */}
      {showDeliver && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowDeliver(false)} />
          <div className="relative bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4">Mark as Delivered</h3>
            <div className="mb-4">
              <label className="input-label">Tracking Number (optional)</label>
              <input value={deliverTracking} onChange={(e) => setDeliverTracking(e.target.value)} className="input" placeholder="e.g., 1Z999AA10123456784" />
            </div>
            <div className="mb-4">
              <label className="input-label">Notes (optional)</label>
              <textarea value={deliverNotes} onChange={(e) => setDeliverNotes(e.target.value)} className="input" rows={3} placeholder="Any notes about the delivery..." />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowDeliver(false)} className="btn btn-outline flex-1">Cancel</button>
              <button
                onClick={async () => {
                  await deliverMutation.mutateAsync({ id: donation.id, shippingTracking: deliverTracking || undefined, notes: deliverNotes || undefined });
                  toast.success('Marked as delivered');
                  setShowDeliver(false);
                  setDeliverTracking('');
                  setDeliverNotes('');
                }}
                disabled={deliverMutation.isPending}
                className="btn btn-primary flex-1"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receive modal */}
      {showReceive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowReceive(false)} />
          <div className="relative bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4">Confirm Receipt</h3>
            <p className="text-sm text-spi-slate mb-4">Confirm that you have physically received this seed shipment.</p>
            <div className="mb-4">
              <label className="input-label">Notes (optional)</label>
              <textarea value={receiveNotes} onChange={(e) => setReceiveNotes(e.target.value)} className="input" rows={3} placeholder="Condition of shipment, quantity received, etc." />
            </div>
            <div className="mb-4">
              <label className="input-label">Proof Images (optional, up to 5)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []).slice(0, 5);
                  setReceiveFiles(files);
                }}
                className="input text-sm"
              />
              {receiveFiles.length > 0 && <p className="text-xs text-spi-slate mt-1">{receiveFiles.length} file(s) selected</p>}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowReceive(false)} className="btn btn-outline flex-1">Cancel</button>
              <button
                onClick={async () => {
                  await receiveMutation.mutateAsync({ id: donation.id, notes: receiveNotes || undefined, files: receiveFiles.length > 0 ? receiveFiles : undefined });
                  toast.success('Receipt confirmed');
                  setShowReceive(false);
                  setReceiveNotes('');
                  setReceiveFiles([]);
                }}
                disabled={receiveMutation.isPending}
                className="btn btn-primary flex-1"
              >
                Confirm Receipt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* IRS Form Modal */}
      {showIrsModal && (
        <IrsFormModal
          donationId={donation.id}
          existingFormData={irsModalExistingData}
          onClose={() => { setShowIrsModal(false); setIrsModalExistingData(null); }}
          onGenerated={handleIrsModalGenerated}
        />
      )}

      {/* PDF Preview Modal */}
      {showPreview && previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowPreview(false)} />
          <div className="relative bg-white rounded-2xl p-6 max-w-3xl w-full mx-4 shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">IRS Form 8283 Preview</h3>
              <button onClick={() => setShowPreview(false)} className="text-spi-slate hover:text-spi-charcoal">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <iframe
              src={previewUrl}
              title="IRS Form 8283 Preview"
              className="flex-1 w-full rounded-lg border border-gray-200"
              style={{ minHeight: '500px' }}
            />
            <div className="flex gap-3 mt-4 justify-end">
              <button
                onClick={async () => {
                  setDownloading(true);
                  try {
                    await downloadIrsForm(donation.id);
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : 'Download failed');
                  } finally {
                    setDownloading(false);
                  }
                }}
                disabled={downloading}
                className="btn btn-outline"
              >
                {downloading ? 'Downloading...' : 'Download'}
              </button>
              {isAdmin && (
                <button
                  onClick={handleEmail}
                  disabled={emailIrsMutation.isPending}
                  className="btn btn-primary"
                >
                  {emailIrsMutation.isPending ? 'Sending...' : 'Email to Donor'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showCancel}
        title="Cancel Donation"
        message="Are you sure you want to cancel this donation?"
        confirmLabel="Cancel Donation"
        danger
        onConfirm={async () => {
          await cancelMutation.mutateAsync(donation.id);
          toast.success('Donation cancelled');
          setShowCancel(false);
        }}
        onCancel={() => setShowCancel(false)}
      />
    </div>
  );
}
